<template>
  <v-card class="conversation">
    <div class="conversation-content" ref="bubblesWrapper" >
      <div class="top-bar">
        <PopHover type="primary" bottom v-if="reminder" :showOnClick="isMobile">
          <TopBarAction icon="icon-ex-fill-s" :disabled="isLoading"/>
          <template slot="pop-hover-header">
            <div class="reminder-pophover-header">{{$t('client.matters.matter_page.conversation_tab.reminder_tooltip.title')}}</div>
          </template>
          <template slot="pop-hover-content">
            <div class="reminder-content">
              <div class="reminder-header" v-if="reminder.date">{{$t('client.matters.matter_page.conversation_tab.reminder_tooltip.reminder_set', {time: reminderDate})}}</div>
              <div class="reminder-header" v-else>{{$t('client.matters.matter_page.conversation_tab.reminder_tooltip.just_follow')}}</div>
              <div class="reminder-text" v-if="reminder.message">{{reminder.message}}</div>
            </div>
          </template>
        </PopHover>
      </div>
      <div v-if="channels.length > 0">
        <infinite-loading direction="top" :identifier="refreshId" @infinite="infiniteHandler">
          <div slot="spinner">
            <VcLoader/>
          </div>
          <div slot="no-more"></div>
          <div slot="no-results"></div>
        </infinite-loading>
        <div class="no-results-wrapper" v-if="!isLoading && messages.length === 0">
          <NoResultsCmp v-if="messagesError"
                        icon="icon-warning"
                        :text="$t('client.matters.conversation.messages_error')"
                        :action-text="$t('client.matters.conversation.try_again')"
                        @action-click="refresh"/>
          <NoResultsCmp v-else-if="completed"
                        icon="icon-conversation"
                        :text="$t('client.matters.conversation.messages_no_results')" />
        </div>

        <div class="bubble-row"
            v-for="(msg ,i) in messagesToDisplay"
            :key="msg.uid">
          <div class="bubble-wrapper">
            <BubbleMsg :message="msg"
                       :class="{'full-width': isSmallScreen}"
                       :show-staff="isMultipleStaffs"
                       :show-channel="true"
                       @event-fired="onEventFired"/>
            <div ref="vcitaDivider"
                v-if="displayDivider(msg, i)">
              <VcitaDivider
                data-qa="vcita-divider"
                :text="dividerText"
                :color="dividerColor" />
            </div>

          </div>
        </div>

        <div class="bubble-row" v-if="hasClientTypingIndicatorFF && userIsTyping">
          <div class="bubble-wrapper">
            <BubbleMsg :message="{direction: 'client_to_business'}"
                       :class="{'full-width': isSmallScreen, 'typing': true}"
                       :show-staff="false"
                       is-typing />
          </div>
        </div>

      </div>

      <div class="scroll-to-bottom-btn" v-if="displayScrollDownBtn">
        <VcitaButtonWithBadge
          data-qa="vcita-button-with-badge"
          @click="scrollToBottom"
          :bdgBackgroundColor="dividerColor"
          :bdgText="newMessagesCount <= 9 ? newMessagesCount.toString() : '9+'"
          btnIcon="icon-Temp_Chevron-down-16"
          :showBadge="!!newMessagesCount"/>
      </div>
    </div>

    <ConversationFooter
      v-on:sending-message='sendingMessageHandler'
      :isLoading="isLoading"
      :isPane="isPane"
      :isSmallScreen="isSmallScreen"
      :readyForChannelSelector="readyForChannelSelector"/>
  </v-card>
</template>

<script>
import BubbleMsg from "@/modules/matters/components/conversation/bubbles/BubbleMsg";
import TopBarAction from "@/modules/matters/components/conversation/TopBarAction";
import {DropdownMenu, NoResultsCmp, PopHover} from "@/modules/common/components"
import {mapGetters} from 'vuex'
import {markFirstAndLast} from '@/modules/matters/filters'
import InfiniteLoading from 'vue-infinite-loading';
import OptionsSelector from "@/modules/common/components/OptionsSelector/OptionsSelector";
import realtimeMessagingService from "@/modules/common/services/realtimeMessagingService"
import VcitaDivider from "@/modules/common/components/VcitaDivider/VcitaDivider";
const debouncer = require('just-debounce');
import VcitaButtonWithBadge from "@/modules/common/components/VcitaButtonWithBadge/VcitaButtonWithBadge";
import ConversationFooter from "../../conversation/ConversationFooter";


export default {
  name: 'Conversation',
  props: {
    matter: {
      type: Object,
      required: true
    },
    isSmallScreen: {
      type: Boolean,
      default: false
    },
    isPane: {
      type: Boolean
    }
  },
  components: {
    ConversationFooter,
    BubbleMsg,
    DropdownMenu,
    InfiniteLoading,
    TopBarAction,
    NoResultsCmp,
    PopHover,
    OptionsSelector,
    VcitaDivider,
    VcitaButtonWithBadge
  },
  filters: {
    markFirstAndLast,
  },
  data() {
    return {
      completed: false,
      isLoading: true,
      refreshId: +new Date(),
      dividerText: this.$t('client.matters.conversation.divider_text'),
      dividerColor: "#F2514A",
      shouldDisplayDivider: false,
      dividerIsActive: false,
      displayScrollDownBtn: false,
      newMessagesCount: 0,
      latestOldMessage: null,
      messageHeight: 45,
      realTimeMessagingTO: null,
      conversationIsActive: false,
      isTextAreaFocus: false,
      userIsTyping: false,
    }
  },
  async created() {
    this.$frontageApi.listen('fetch-new-messages', this.onRefreshEvent);
    this.$frontageApi.listen('reminder-event', this.updateReminder);

    await this.fetchFollowUpFromCollaborators()
    this.isLoading = false;
    this.conversationIsActive = true;
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters('mattersModule', [
      'currContact',
      'collaborators',
      'conversationLatestUpdate',
      'messagesPage',
      'messages',
      'reminder'
    ]),
    ...mapGetters('accountModule', ['staff', 'isMultipleStaffs', 'business', 'channels']),
    readyForChannelSelector() {
      return !this.isLoading && !!this.currContact
    },
    messagesToDisplay() {
      const messages = this.$options.filters.markFirstAndLast(this.messages)
      this.$frontageApi.send('update-follow-up-button', {messageCount: this.messages.length})
      return messages.slice().reverse()
    },
    client() {
      return this.$store.getters['mattersModule/currContact']
    },
    reminderDate() {
      return this.$options.filters.time_today_or_date(this.reminder.date)
    },
    messagesError() {
      return this.$store.state.mattersModule.conversation.error
    },
    hasClientTypingIndicatorFF() {
      return this.$hasFeature('client_typing_indicator')
    },
  },
  methods: {
    async refresh() {
      this.$store.dispatch({type: 'mattersModule/resetConversation'})
      this.refreshId = +new Date();
    },
    sendingMessageHandler (val){
      this.isLoading = val
      !val && this.scrollToBottom()
    },
    checkNewMessages() {
      if (this.messagesToDisplay && this.messagesToDisplay.length > 0 && this.latestOldMessage) {
        this.dividerIsActive = true
      }
    },
    displayDivider(msg, i) {
      let findLastReadMessage = false
      if (this.latestOldMessage){
        findLastReadMessage = msg.uid === this.latestOldMessage.uid && i !== this.messagesToDisplay.length-1 && this.messagesToDisplay[i+1].direction === 'client_to_business'
      }

      return this.dividerIsActive && findLastReadMessage
    },
    setLatestMsg() {
      if(!this.latestOldMessage && this.messages[0] && this.messages[0].was_read === false){
        this.latestOldMessage = this.messages.find(msg => msg.was_read === true)
      }
    },
    onConversationScroll() {
      const element = this.$refs.bubblesWrapper
      const scrollPosition = element.scrollHeight - element.scrollTop - element.clientHeight
      const conversationScrolledUp = scrollPosition >= this.messageHeight
      const conversationScrolledDown = !conversationScrolledUp

      if(conversationScrolledDown) {
        if(!this.dividerIsActive) { this.latestOldMessage = null }
        if (this.newMessagesCount > 0) { this.updateBusinessUnread() }
        this.newMessagesCount = 0
      }

      this.displayScrollDownBtn = !!conversationScrolledUp

      if(!this.dividerIsActive) {
        this.shouldDisplayDivider = !!conversationScrolledUp
      }
    },
    scrollToBottom() {
      const conversationElement = this.$refs.bubblesWrapper;
      conversationElement.scroll({ top: conversationElement.scrollHeight , behavior: 'smooth' });
    },
    scrollToDivider() {
      const element = this.$refs.vcitaDivider && this.$refs.vcitaDivider[0]
      if ( element ) {
        const conversationElement = this.$refs.bubblesWrapper;
        conversationElement.scroll({ top: element.offsetTop - this.messageHeight });
      }
    },
    async fetchNew(getAll = false, getMessages = true) {

      const currNumOfMessages = this.messages.length
      const element = this.$refs.bubblesWrapper;

      const shouldScrollToBottom = element.scrollHeight - Math.abs(element.scrollTop) - element.clientHeight < this.messageHeight

      if (getMessages) await this.$store.dispatch({type: 'mattersModule/fetchNewMessageUpdates', payload: {getAll: getAll}})
      if (this.messages.length > currNumOfMessages ){
        this.dividerIsActive = this.dividerIsActive || this.shouldDisplayDivider
        this.newMessagesCount = this.messages.length - currNumOfMessages + this.newMessagesCount
        this.setLatestMsg()

        if (shouldScrollToBottom) {
          element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
        }
      }
      this.realTimeMessagingLongPollDebouncer()
    },
    updateBusinessUnread() {
      const data = {business_unread: false}
      this.$store.dispatch({type: 'mattersModule/updateConversation', payload: {data}})
    },
    notEnoughResultsForScroll(){
      return this.messages.length > 0 && this.messages.length < this.$store.state.mattersModule.conversation.filterBy.per_page
    },
    async infiniteHandler($state) {
      if (this.notEnoughResultsForScroll()) {
        $state.complete();
        return
      }
      this.isLoading = true
      const page = this.messagesPage
      this.$store.commit('mattersModule/setMessagesPage', page + 1)
      await this.$store.dispatch({type: 'mattersModule/loadMessages'})

      this.updateBusinessUnread()

      this.setLatestMsg()

      this.checkNewMessages()

      const completed = this.$store.state.mattersModule.conversation.completed

      if (completed || this.messagesError) {
        $state.complete();
        this.isLoading = false
        this.completed = true;
        return
      }
      $state.loaded();
      this.isLoading = false
      if (page === 0){
        setTimeout(this.realTimeMessagingLongPollDebouncer, 2000);
        this.scrollToDivider()
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        const element = this.$refs.bubblesWrapper;
        element.addEventListener("scroll", this.onConversationScroll);
      }
    },
    onEventFired(event) {
      if (event.action === 'open-tab') {
        this.$emit('open-tab', {tab: event.tab})
      }
    },
    onRefreshEvent({clientUid}) {
      if (this.client.uid !== clientUid) return

      this.refresh()
    },
    updateReminder({clientUid, reminder}) {
      if (this.matter.uid === clientUid) {
        this.$store.commit('mattersModule/setReminder', reminder)
      }
    },
    async fetchFollowUpFromCollaborators() {
      await this.$store.dispatch({type: 'mattersModule/fetchCollaborators'});
      const current_collaborator = this.collaborators.find(collaborator => collaborator.staff_uid === this.staff.uid)
      if (!current_collaborator || !current_collaborator.reminder) return
      this.$store.commit('mattersModule/setReminder', current_collaborator.reminder)
    },
    async realTimeMessagingLongPoll(){
      realtimeMessagingService.cancelRequest()
      let timestamp = this.conversationLatestUpdate

      if(this.messages.length === 0 || !timestamp || timestamp <= 0) {
        timestamp = Math.floor(new Date()/1000)
      }

      try {
        const realTimeParam = {
          key: this.business.uid + '/' + this.matter.uid,
          timestamp
        };
        if(this.hasClientTypingIndicatorFF) {
          realTimeParam.typing = this.userIsTyping
        }
        const res = await realtimeMessagingService.fetchLatestUpdateTime(realTimeParam)
        if (res.data?.message) {
          if(this.hasClientTypingIndicatorFF) {
            this.userIsTyping = !!res.data.message.typingIndicator;
          }
          if(res.data.message.updated_at) {
            await this.$store.dispatch({
              type: 'mattersModule/setLastUpdate',
              payload: {timestamp: res.data.message.updated_at}
            })
            await this.fetchNew()
          }
          this.realTimeMessagingLongPollDebouncer()
        } else {
          this.userIsTyping = false
          this.realTimeMessagingLongPollDebouncer()
        }
      } catch (error) {
        this.userIsTyping = false
        this.handleMessagingLongPollError(error)
      }
    },
    handleMessagingLongPollError(error) {
      if (!this.isValidLongPollError(error)){
        console.error('realTimeMessagingLongPoll: ', error.message)
        clearTimeout(this.realTimeMessagingTO)
        this.realTimeMessagingTO = setTimeout(this.realTimeMessagingLongPollDebouncer, 30000)
      }
      if (this.isConnectionAbortedError(error) || this.isConnectionTimeoutError(error)) {
        this.realTimeMessagingLongPollDebouncer()
      }
    },
    isValidLongPollError(error) {
      if (!error) return false
      return this.isConnectionAbortedError(error) || this.isConnectionCanceledError(error) || this.isConnectionTimeoutError(error)
    },
    isConnectionAbortedError(error) {
      return error.code && error.code === 'ECONNABORTED'
    },
    isConnectionCanceledError(error) {
      return error.message && error.message === 'cancel'
    },
    isConnectionTimeoutError(error) {
      return error.message && error.message === 'timeout'
    },
    realTimeMessagingLongPollDebouncer() {
      if (!this.conversationIsActive) return
      debouncer(this.realTimeMessagingLongPoll, 1000)();
    },
    handleVisibilityChange() {
      if (this.conversationIsActive !== !document.hidden) {
        this.conversationIsActive = !document.hidden
        this.conversationIsActive ? this.fetchNew(true) : realtimeMessagingService.cancelRequest()
      }
    }
  },
  destroyed() {
    this.updateBusinessUnread()
    this.conversationIsActive = false;
    this.$store.dispatch({type: 'mattersModule/resetConversation'})
    this.$frontageApi.remove('fetch-new-messages', this.onRefreshEvent)
    this.$frontageApi.remove('reminder-event', this.updateReminder);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    realtimeMessagingService.cancelRequest()
    clearTimeout(this.realTimeMessagingTO)
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/abstract/_mixins.scss";
@import "@/assets/scss/abstract/_variables.scss";

.reminder-content {
  font-size: 12px;

  .reminder-header {
    font-weight: 500;
    margin: 0 0 7px 0;
    line-height: 1.3;
  }
}

.conversation {
  box-shadow: none;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .top-bar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    padding: 5px;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;

    > * {
      margin: 0 3px;
    }

    &:last-child {
      margin: 0;
    }
  }

  .scroll-to-bottom-btn {
    position: sticky;
    bottom: 10px;
    align-self: end;
  }

  .conversation-content {
    padding: 22px 15px 15px 15px;
    flex: 1;
    overflow-y: scroll;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;

    .no-results-wrapper {
      height: 100%;
    }

    .vcita-divider {
      margin-inline: -21px;
    }

    .bubble-wrapper {
      display: flex;
      flex-direction: column;
      margin: 6px;

      .bubble-msg {
        max-width: 70%;

        &.full-width {
          max-width: 90%;
        }

        &.client_to_business {
          align-self: flex-start;
        }

        &.to_business {
          align-self: center;
        }

        &.business_to_client {
          align-self:flex-end;
        }
      }
    }
  }
}
</style>
